import { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { validate as isValidUUID } from 'uuid';

import { useTenant } from '../../../../app/context/tenant-context';
import { getCollection } from '../../../../app/services/tenants';
import { useTabManager } from '../../../context/tab-manager-context';

import { useToast } from '@controlrooms/components';

/**
 * AppCore - URL-based View/Collection Loader
 *
 * This component has ONE job: Load views/collections from URL when they don't exist in tabs.
 *
 * Design Principles:
 * 1. ONLY handles loading NEW views/collections from URL
 * 2. Does NOT handle tab switching (tab-manager-context does that)
 * 3. Does NOT handle URL syncing (tab-manager-context does that)
 * 4. Does NOT handle base URL redirects (tab-manager-context does that)
 *
 * When to act:
 * - URL contains a viewId that is NOT in the current tabs AND is NOT a UUID
 * - URL contains a collection hash
 *
 * When to skip:
 * - Base URL (no viewId)
 * - ViewId already exists in tabs
 * - ViewId is a UUID (unsaved tab that should exist locally)
 */
export const AppCore: React.FC = () => {
  const { tenant } = useTenant();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();

  const { tabs, handleTenantSwitch, openSavedView, addNewTab, loadCollection } = useTabManager();

  // Track previous tenant to detect tenant switches
  const previousTenantRef = useRef<number | undefined>(undefined);

  // Track if we're currently loading a view to prevent duplicate calls
  const isLoadingRef = useRef(false);

  // Track failed viewIds to prevent infinite retries
  const failedViewIdsRef = useRef<Set<string>>(new Set());

  // Track the last URL we processed to avoid re-processing
  const lastProcessedUrlRef = useRef<string>('');

  // Track the previous tabs length to detect if this is a close operation
  const prevTabsLengthRef = useRef<number>(tabs.length);

  // ============================================================================
  // HELPERS
  // ============================================================================

  /**
   * Parse viewId from URL path
   * Returns null for reserved segments (collections, alerts, manage, etc.)
   */
  const parseViewIdFromPath = useCallback(
    (path: string): string | null => {
      const tenantPath = `/app/t/${tenant}`;
      if (!path.startsWith(tenantPath)) return null;

      const remainder = path.slice(tenantPath.length);
      if (!remainder || remainder === '/') return null;

      const segments = remainder.split('/').filter(Boolean);
      if (segments.length === 0) return null;

      const firstSegment = segments[0];

      // Reserved segments that are not viewIds
      const reservedSegments = [
        'c', // collection
        'alerts',
        'alert-manager',
        'limits',
        'manage',
        'views',
        'analyze',
      ];

      if (reservedSegments.includes(firstSegment)) {
        return null;
      }

      return firstSegment;
    },
    [tenant],
  );

  // ============================================================================
  // TENANT SWITCH HANDLER
  // ============================================================================
  useEffect(() => {
    const previousTenant = previousTenantRef.current;
    if (previousTenant !== undefined && previousTenant !== tenant) {
      // Clear failed viewIds on tenant switch
      failedViewIdsRef.current.clear();
      lastProcessedUrlRef.current = '';
      handleTenantSwitch();
      // Navigate to new tenant's base path
      navigate(`/app/t/${tenant}`, { replace: true });
    }
    previousTenantRef.current = tenant;
  }, [tenant, handleTenantSwitch, navigate]);

  // ============================================================================
  // URL-BASED VIEW LOADER
  // Only loads views that don't exist in tabs yet
  // ============================================================================
  useEffect(() => {
    const path = location.pathname;
    const tenantPath = `/app/t/${tenant}`;
    const prevTabsLength = prevTabsLengthRef.current;

    // Update prev tabs length ref
    prevTabsLengthRef.current = tabs.length;

    // If tabs decreased (close operation), skip URL processing
    // The URL will be updated by tab-manager-context, we'll process on next run
    if (tabs.length < prevTabsLength) {
      return;
    }

    // Skip if we already processed this exact URL
    if (lastProcessedUrlRef.current === path) {
      return;
    }

    // Skip base URL - tab-manager-context handles redirects
    if (path === tenantPath || path === `${tenantPath}/`) {
      lastProcessedUrlRef.current = path;
      return;
    }

    // Parse viewId from URL
    const viewIdFromPath = parseViewIdFromPath(path);

    if (viewIdFromPath) {
      // Skip if this viewId already failed (prevent infinite retries)
      if (failedViewIdsRef.current.has(viewIdFromPath)) {
        lastProcessedUrlRef.current = path;
        return;
      }

      // Skip if currently loading
      if (isLoadingRef.current) {
        return;
      }

      // Check if tab already exists - if so, skip (tab-manager handles switching)
      const existingTab = tabs.find((t) => t.id === viewIdFromPath || t.viewId === viewIdFromPath);
      if (existingTab) {
        lastProcessedUrlRef.current = path;
        return;
      }

      // Check if it's a UUID (unsaved tab that should exist locally)
      const isUnsavedTabId = isValidUUID(viewIdFromPath);
      if (isUnsavedTabId) {
        // UUID but not in tabs - stale/invalid URL
        // Navigate to base path, let tab-manager handle redirect to active tab
        lastProcessedUrlRef.current = path;
        navigate(tenantPath, { replace: true });
        return;
      }

      // Handle newSession query param (opened in new window)
      const isNewSession = searchParams.get('newSession') === 'true';
      if (isNewSession) {
        searchParams.delete('newSession');
        setSearchParams(searchParams, { replace: true });

        const createNewSessionTab = async () => {
          // For new session, always create a fresh tab
          await addNewTab();
        };

        createNewSessionTab().catch((error) => {
          console.error('Failed to create new session tab:', error);
        });
        lastProcessedUrlRef.current = path;
        return;
      }

      // Not a UUID, not in tabs - it's a saved view ID, fetch from API
      lastProcessedUrlRef.current = path;
      isLoadingRef.current = true;

      openSavedView(viewIdFromPath)
        .catch((error) => {
          console.error('Failed to load view from URL:', error);
          failedViewIdsRef.current.add(viewIdFromPath);
          toast.error('Unable to load the requested view');
          // Navigate to base path on error
          navigate(tenantPath, { replace: true });
        })
        .finally(() => {
          isLoadingRef.current = false;
        });

      return;
    }

    // Collection URL - /app/t/:tenantId/c/:hash
    if (path.includes('/c/')) {
      const segments = path.split('/');
      const cIndex = segments.indexOf('c');
      const collectionHash = cIndex !== -1 ? segments[cIndex + 1] : null;

      if (collectionHash) {
        // Skip if already failed
        if (failedViewIdsRef.current.has(`collection:${collectionHash}`)) {
          lastProcessedUrlRef.current = path;
          return;
        }

        // Skip if currently loading
        if (isLoadingRef.current) {
          return;
        }

        lastProcessedUrlRef.current = path;
        isLoadingRef.current = true;

        // Fetch collection and load it
        getCollection(collectionHash)
          .then((persistCollection) => {
            return loadCollection(persistCollection);
          })
          .catch((error) => {
            console.error('Failed to load collection from URL:', error);
            failedViewIdsRef.current.add(`collection:${collectionHash}`);
            toast.error('Unable to load the collection');
            // Navigate to base path on error
            navigate(tenantPath, { replace: true });
          })
          .finally(() => {
            isLoadingRef.current = false;
          });
      }
      return;
    }

    // Legacy routes - /app/t/:tenantId/views
    if (path === `${tenantPath}/views`) {
      lastProcessedUrlRef.current = path;
      if (tabs.length === 0) {
        addNewTab().catch((error) => {
          console.error('Failed to create new tab:', error);
        });
      }
      return;
    }

    // Update last processed for any other path
    lastProcessedUrlRef.current = path;
  }, [
    location.pathname,
    tenant,
    tabs,
    parseViewIdFromPath,
    navigate,
    searchParams,
    setSearchParams,
    openSavedView,
    addNewTab,
    loadCollection,
    toast,
  ]);

  // This component doesn't render anything - it's purely for URL handling
  return null;
};
